import type { ChurchParams } from './churchParams';
import { elementaryRanges, spanOver, type PlanFrame } from './planFrame';
import {
  CHURCH_PLAN,
  buttressRoom,
  isHall,
  type ChapelPlan,
  type Side,
  type SidePlan,
  type TranseptPlan,
  type VesselPlan,
} from './churchModel';

interface Run {
  readonly u0: number;
  readonly u1: number;
  readonly depth: number;
}

function depthBeyond(frame: PlanFrame, range: readonly [number, number], vc: number, wall: number, side: Side): number {
  const span = spanOver(frame, range[0], range[1], vc);
  if (!span) return -Infinity;
  return side * ((side > 0 ? span[1] : span[0]) - wall);
}

function depthClass(depth: number, target: number): 'full' | 'narrow' | 'none' {
  if (depth >= CHURCH_PLAN.fullSideShare * target) return 'full';
  return depth >= CHURCH_PLAN.minSide ? 'narrow' : 'none';
}

/**
 * Stretches along the part where the footprint reaches past the wall, grouped by whether
 * they take the full target width or step in to a narrower one.
 */
function sideRuns(
  frame: PlanFrame,
  part: readonly [number, number],
  wall: { readonly vc: number; readonly v: number; readonly side: Side },
  target: number
): Run[] {
  const runs: (Run & { kind: string })[] = [];
  for (const range of elementaryRanges(frame, part[0], part[1])) {
    const depth = depthBeyond(frame, range, wall.vc, wall.v, wall.side);
    const kind = depthClass(depth, target);
    const last = runs[runs.length - 1];
    if (kind === 'none') continue;
    if (last && last.kind === kind && last.u1 === range[0]) {
      runs[runs.length - 1] = { kind, u0: last.u0, u1: range[1], depth: Math.min(last.depth, depth) };
    } else {
      runs.push({ kind, u0: range[0], u1: range[1], depth });
    }
  }
  return runs.filter((r) => r.u1 - r.u0 >= CHURCH_PLAN.minRun);
}

function sideRoof(params: ChurchParams, vessel: VesselPlan, width: number): Pick<SidePlan, 'eaveY' | 'topY' | 'roof'> {
  const c = CHURCH_PLAN;
  if (isHall(params)) {
    return { eaveY: vessel.eaveY, topY: vessel.eaveY + (width / 2) * Math.tan(c.roofPitch), roof: 'gable' };
  }
  const eaveY = params.aisleHeight;
  const cap = vessel.eaveY - c.clerestoryShare * (vessel.eaveY - eaveY);
  return { eaveY, topY: Math.min(eaveY + width * Math.tan(c.leanToPitch), cap), roof: 'leanTo' };
}

export function planSides(
  frame: PlanFrame,
  params: ChurchParams,
  vessel: VesselPlan,
  transept: TranseptPlan | null
): SidePlan[] {
  if (params.aisles < 1) return [];
  const target = isHall(params)
    ? (params.naveWidth ?? 6 * vessel.half) / 3
    : CHURCH_PLAN.aisleRatio * 2 * vessel.half;
  const parts: [number, number][] = transept
    ? [
        [vessel.u0, transept.u0],
        [transept.u1, vessel.chordU],
      ]
    : [[vessel.u0, vessel.chordU]];
  return parts.flatMap((part) =>
    ([1, -1] as const).flatMap((side) => {
      const inner = vessel.vc + side * vessel.half;
      return sideRuns(frame, part, { vc: vessel.vc, v: inner, side }, target).map((run): SidePlan => {
        const room = isHall(params) ? 0 : buttressRoom();
        const width = Math.min(target, run.depth - room);
        return { side, u0: run.u0, u1: run.u1, inner, outer: inner + side * width, ...sideRoof(params, vessel, width) };
      });
    })
  );
}

function chapelHeights(run: SidePlan, bay: number): { eaveY: number; ridgeY: number } {
  const c = CHURCH_PLAN;
  const rise = (bay / 2) * Math.tan(c.chapelPitch);
  const eaveY =
    run.roof === 'gable'
      ? c.chapelEaveRatio * run.eaveY
      : Math.max(c.minChapelEave, run.eaveY - rise - c.chapelClearance);
  return { eaveY, ridgeY: eaveY + rise };
}

function chapelRow(frame: PlanFrame, vessel: VesselPlan, run: SidePlan): ChapelPlan[] {
  const c = CHURCH_PLAN;
  const count = Math.max(1, Math.round((run.u1 - run.u0) / c.chapelBay));
  const bay = (run.u1 - run.u0) / count;
  const heights = chapelHeights(run, bay);
  const chapels: ChapelPlan[] = [];
  for (let k = 0; k < count; k++) {
    const range: [number, number] = [run.u0 + k * bay, run.u0 + (k + 1) * bay];
    const depth = depthBeyond(frame, range, vessel.vc, run.outer, run.side) - buttressRoom();
    if (depth < c.minChapelDepth) continue;
    const outer = run.outer + run.side * Math.min(depth, c.maxChapelDepth);
    chapels.push({ side: run.side, u0: range[0], u1: range[1], inner: run.outer, outer, ...heights });
  }
  return chapels;
}

export function planChapels(
  frame: PlanFrame,
  params: ChurchParams,
  vessel: VesselPlan,
  sides: readonly SidePlan[]
): ChapelPlan[] {
  if (params.aisles < 1) return [];
  return sides.flatMap((run) => chapelRow(frame, vessel, run));
}
